import axios from 'axios';
import { installRequestID, installSignedRequestAuth, installTenantID } from '@/api/interceptors';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { publicError } from 'shared/constants.json';

const BASE_URL = '/api/v1';
const statusUnauthorized = 401;
const statusTooManyRequests = 429;
const statusServiceUnavailable = 503;
const maxRetryAttempts = 2;

export const httpclient = axios.create({ baseURL: BASE_URL });

const HttpClientPlugin = {
  install: (app, { store }) => {
    if (!store) {
      throw new Error('HttpClientPlugin requires a store instance.');
    }

    // --- Request Interceptor ---
    httpclient.interceptors.request.use(installRequestID);
    httpclient.interceptors.request.use(installTenantID(store));
    httpclient.interceptors.request.use(installSignedRequestAuth, (error) => Promise.reject(error));

    // --- Helper Functions ---
    const setVersion = (header) => {
      const version = header['api-version-sha'];
      const semverVersion = header['api-version'];
      if (isNullOrUndefined(version) || isNullOrUndefined(semverVersion)) return;

      store.commit('APP_VERSION_RETRIEVED', { version, semverVersion });
    };

    const sendToLogin = (status, config) => {
      if (status !== statusUnauthorized) return false;
      return !['/api/v1/logout', '/logout'].includes(config.url);
    };

    // --- Response Interceptor ---
    httpclient.interceptors.response.use(
      (response) => {
        setVersion(response.headers);
        return response;
      },
      async (error) => {
        if (isNullOrUndefined(error.response)) {
          // we are probably offline
          // TODO: add offline handler
          return Promise.reject(error);
        }

        const { status, data, headers, config } = error.response;
        setVersion(headers);

        // 429 Retry Logic
        if (status === statusTooManyRequests && headers['retry-after']) {
          const cfg = config || {};
          cfg.__retryCount = cfg.__retryCount || 0;
          if (cfg.__retryCount < maxRetryAttempts) {
            cfg.__retryCount += 1;
            const ra = parseInt(headers['retry-after'], 10);
            const delayMs = Number.isNaN(ra) ? 3000 : ra * 1000;

            await new Promise((resolve) => { setTimeout(resolve, delayMs); });
            return httpclient(cfg);
          }
        }

        // Maintenance Mode
        if (status === statusServiceUnavailable && typeof data === 'string' && data.includes(publicError.maintenanceMode)) {
          setTimeout(() => {
            store.commit('ENABLE_MAINTENANCE_MODE');
          }, 100);
        }

        // Unauthorized / Login redirect
        if (sendToLogin(status, config)) {
          setTimeout(() => {
            store.commit('USER_NOT_LOGGED_IN');
          }, 100);
        }

        return Promise.reject(error);
      },
    );
  },
};

export default HttpClientPlugin;
